import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { BoostaBolt } from '@/components/BoostaBolt';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSent(true);
      if (data.dev_reset_link) setDevLink(data.dev_reset_link);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-boosta-ink flex items-center justify-center px-4 relative overflow-hidden aurora">
      <div className="absolute inset-0 grid-bg opacity-30"/>
      <div className="absolute inset-0 grain"/>
      <div className="absolute top-6 left-6 z-10">
        <Link to="/" className="flex items-center gap-2"><BoostaBolt size={28} animate/><span className="font-display font-bold text-3d">Boosta</span></Link>
      </div>
      <div className="relative w-full max-w-md z-10">
        <div className="glass rounded-3xl p-8 md:p-10" data-testid="forgot-root">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="w-14 h-14 rounded-2xl bg-boosta-emerald/10 flex items-center justify-center mx-auto mb-4"><MailCheck className="w-6 h-6 text-boosta-emerald"/></div>
              <h1 className="font-display font-black text-2xl text-3d">Check your inbox</h1>
              <p className="text-white/55 text-sm mt-2">If an account exists for <span className="text-boosta-cyan">{email}</span>, we've sent a reset link. It expires in 30 minutes.</p>
              {devLink && (
                <div className="mt-5 text-left text-xs bg-black/30 border border-boosta-yellow/25 rounded-xl p-3" data-testid="forgot-dev-link">
                  <div className="text-boosta-yellow font-bold mb-1">Dev mode (no email provider configured)</div>
                  <a href={devLink} className="text-boosta-cyan break-all underline">{devLink}</a>
                </div>
              )}
              <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-boosta-cyan hover:underline"><ArrowLeft className="w-4 h-4"/> Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Password reset</div>
              <h1 className="font-display font-black text-3xl tracking-tight text-3d">Forgot password?</h1>
              <p className="text-white/50 text-sm mt-1.5">Enter your email and we'll send you a secure reset link.</p>
              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block mt-8">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="forgot-email"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:border-boosta-cyan/50 outline-none transition" placeholder="you@company.com"/>
              </div>
              <button disabled={busy} type="submit" data-testid="forgot-submit"
                className="mt-6 w-full btn-primary rounded-xl py-3.5 font-bold disabled:opacity-40">
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
              <div className="mt-6 text-center">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-white/45 hover:text-boosta-cyan"><ArrowLeft className="w-4 h-4"/> Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
